import React, { cloneElement } from 'react';
import { usePopper } from 'react-popper';
import { useClickOutside, useKeyPress } from '@Hooks';

const DropdownContext = React.createContext({
    isToggled: false
});

function useDropdownContext() {
    const context = React.useContext(DropdownContext);
    if (!context) {
        throw new Error('Components cannot be rendered outside the Dropdown component');
    }
    return context;
}

const Toggle = ({ children }) => {
    const { setPopperReference } = useDropdownContext();
    return cloneElement(children, { ref: setPopperReference });
};

const DropdownContent = props => {
    const { children, contentStyles = {} } = props;
    const { isToggled, setPopperElement, popper } = useDropdownContext();

    const { styles: popperStyles, attributes: popperAttributes } = popper;
    if (isToggled) {
        return (
            <div
                ref={setPopperElement}
                style={{ ...popperStyles.popper, top: 5 }}
                css={contentStyles}
                {...popperAttributes.popper}
            >
                {children}
            </div>
        );
    }

    return null;
};

function Dropdown(props) {
    const {
        children,
        closeOnClickOutSide = true,
        open = false,
        onDismiss,
        closeOnEscape = true,
        placement = 'bottom-start',
        ...rest
    } = props;

    const [popperReference, setPopperReference] = React.useState(null);
    const [popperElement, setPopperElement] = React.useState(null);
    const popper = usePopper(popperReference, popperElement, { placement });

    const wrapperRef = useClickOutside(() => {
        if (closeOnClickOutSide) {
            onDismiss && onDismiss(!open, 'ClickOutSide');
        }
    });

    useKeyPress('Escape', () => {
        if (closeOnEscape) {
            onDismiss && onDismiss(!open, 'EscapeKey');
        }
    });

    return (
        <div ref={wrapperRef} style={{ top: 5 }} {...rest}>
            <DropdownContext.Provider
                value={{ isToggled: open, popper, setPopperReference, setPopperElement }}
            >
                {children}
            </DropdownContext.Provider>
        </div>
    );
}

Dropdown.Content = DropdownContent;
Dropdown.Toggle = Toggle;

export { Dropdown };
